// Auth utility for managing tokens and authentication
import { API_ENDPOINTS, getApiBaseUrl } from '../config/api';

// Cookie helpers
export const setCookie = (name: string, value: string, days: number = 30) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict;Secure`;
};

export const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

// Token management — reactive refresh pattern (matches Flutter app).
// We do NOT track expiry on the client. We let the token be used until the
// backend returns 401, then we refresh and retry. This removes all hardcoded
// TTL assumptions and background-timer fragility.

export const saveTokens = (authToken: string, refreshToken: string) => {
  localStorage.setItem('authToken', authToken);
  setCookie('refreshToken', refreshToken, 30); // Save refresh token in cookie for 30 days
};

export const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

// "Expired" now simply means "no token at all". Real expiry is discovered
// at request time via a 401 from the backend, which triggers refresh+retry.
export const isTokenExpired = (): boolean => {
  return !localStorage.getItem('authToken');
};

export const getRefreshToken = (): string | null => {
  return getCookie('refreshToken');
};

export const clearTokens = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('authTokenExpiry'); // clean up legacy keys
  localStorage.removeItem('authTokenTimestamp');
  deleteCookie('refreshToken');
};

// Extract userId from JWT payload (used for tezdaCacheUserKey header + ws auth)
const getUserIdFromToken = (token: string): string | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return payload.userId || payload.sub || null;
  } catch {
    return null;
  }
};

// Refresh token logic
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

export const refreshAuthToken = async (): Promise<string | null> => {
  // If already refreshing, wait for it to complete
  if (isRefreshing) {
    return new Promise((resolve) => {
      subscribeTokenRefresh((token: string) => {
        resolve(token);
      });
    });
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.error('No refresh token available');
    return null;
  }

  isRefreshing = true;

  try {
    // Step 1: Get new tokens.
    // IMPORTANT: do NOT send Authorization header on the refresh request.
    // If the current JWT is expired, sending it causes the refresh endpoint
    // itself to return 401 (matches Flutter app pattern).
    // Extract userId from the current auth JWT so the BE can recover our
    // session if the refresh token has been evicted from Redis AND the Mongo
    // `RefreshToken.token` field holds a different token (e.g. rotated by
    // another session, or during Redis OOM eviction). The Flutter app sends
    // the same header — this is what makes mobile robust and the web fragile.
    const currentAuthToken = getAuthToken();
    const cacheUserKey = currentAuthToken ? getUserIdFromToken(currentAuthToken) : null;

    let response;
    try {
      const refreshHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (cacheUserKey) {
        refreshHeaders['Tezdacacheuserkey'] = cacheUserKey;
      }
      response = await fetch(`${getApiBaseUrl()}/auth/v1/refreshTokenV1`, {
        method: 'POST',
        headers: refreshHeaders,
        body: JSON.stringify({
          token: refreshToken,
        }),
      });
    } catch (networkError) {
      console.error('Network error during token refresh:', networkError);
      isRefreshing = false;
      onTokenRefreshed('');
      return null;
    }

    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      console.error('Failed to parse refresh token response:', parseError);
      isRefreshing = false;
      return null;
    }

    // Only 401/403 from the refresh endpoint is a definitive auth failure.
    // Any other non-ok status (500, 502, etc.) is a transient server issue —
    // don't clear tokens or log the user out for those.
    if (response.status === 401 || response.status === 403) {
      console.error('Refresh token rejected by backend:', data?.message);
      clearTokens();
      isRefreshing = false;
      onTokenRefreshed('');
      return null;
    }

    if (!response.ok) {
      console.error('Transient refresh failure, keeping tokens:', response.status, data?.message);
      isRefreshing = false;
      onTokenRefreshed('');
      return null;
    }

    const newAuthToken = data.data?.token;
    const newRefreshToken = data.data?.refreshToken?.token;

    if (!newAuthToken || !newRefreshToken) {
      console.error('Invalid refresh token response shape — keeping tokens');
      isRefreshing = false;
      onTokenRefreshed('');
      return null;
    }

    // Save new tokens
    saveTokens(newAuthToken, newRefreshToken);

    // Step 2: Acknowledge token rotation (if required).
    // Re-derive the userId from the NEW auth JWT so the recovery header stays
    // consistent if the ack gets retried after another eviction.
    if (data.data?.requiresAcknowledgment) {
      try {
        const ackUserKey = getUserIdFromToken(newAuthToken);
        const ackHeaders: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (ackUserKey) {
          ackHeaders['Tezdacacheuserkey'] = ackUserKey;
        }
        await fetch(`${getApiBaseUrl()}/auth/v1/refreshTokenV1`, {
          method: 'POST',
          headers: ackHeaders,
          body: JSON.stringify({
            token: newRefreshToken,
            acknowledgeToken: true,
          }),
        });
      } catch (ackError) {
        // Acknowledgment failure is non-critical
        console.warn('Token acknowledgment failed:', ackError);
      }
    }

    isRefreshing = false;
    onTokenRefreshed(newAuthToken);
    return newAuthToken;
  } catch (error: any) {
    // Unexpected error (network, parse, etc.) — treat as transient, don't log out.
    // The next request will try again and either succeed or hit a real 401.
    console.error('Token refresh error (transient, keeping tokens):', error);
    isRefreshing = false;
    onTokenRefreshed('');
    return null;
  }
};

// Same code name as the tezda-ws guard and tezda-video.
const isModeratorRequired = async (response: Response): Promise<boolean> => {
  try {
    const body = await response.clone().json();
    return !!body && body.code === 'MODERATOR_REQUIRED';
  } catch {
    return false;
  }
};

// API Gateway answers each route's preflight from that route's own header list, and /video/t/* lists neither ws header:
// the browser then refuses the call before sending it. Its routes all read Authorization, so give them only that.
const authHeaders = (url: string, token: string): Record<string, string> => {
  if (Object.values(API_ENDPOINTS).some((base) => url.startsWith(`${base}/`))) {
    return { Authorization: token };
  }
  const userId = getUserIdFromToken(token);
  // userid + accesstoken satisfy the ws auth middleware
  return { Authorization: token, accesstoken: token, ...(userId ? { userid: userId } : {}) };
};

// Enhanced fetch wrapper with automatic token refresh
export const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const authToken = getAuthToken();

  if (!authToken) {
    throw new Error('No authentication token available');
  }

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
    ...authHeaders(url, authToken),
  };

  // Make the request
  let response = await fetch(url, {
    ...options,
    headers,
  });

  // A valid token that simply is not a moderator's: a refresh cannot fix it, and polling pages (every 10 s) would rotate
  // the refresh token every time. Hand the response straight back. Read a CLONE so the caller can still read the body.
  if (response.status === 403 && (await isModeratorRequired(response))) {
    return response;
  }

  // 401 OR 403: refresh and retry once. AWS API Gateway returns 403 with
  // "explicit deny in identity-based policy" for expired tokens at the
  // IAM layer, so both must trigger refresh (matches Flutter pattern).
  if (response.status === 401 || response.status === 403) {
    const newToken = await refreshAuthToken();

    if (!newToken) {
      // Refresh failed, user needs to log in again
      throw new Error('Session expired. Please log in again.');
    }

    // Retry the request with new token
    Object.assign(headers, authHeaders(url, newToken));
    response = await fetch(url, {
      ...options,
      headers,
    });
  }

  return response;
};

// Logout helper
export const logout = () => {
  clearTokens();
  // Redirect to login page
  window.location.href = '/';
};
